import type { SimulatorField } from '@vedalib/editor/lib/elements'
import { useState, useEffect } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { FieldSwitcher, Form, FormField, genDataField } from 'components/formKit'
import { KitSize } from 'components/uiKit/KitTypes'
import { t } from 'services/Translation'

import * as s from './DecoratorSettings.module.scss'

type DecoratorValues = {
  ghost: boolean
  placeholder: boolean
}

const genField = genDataField<DecoratorValues>()

const ghostInputField = genField({
  as: FieldSwitcher,
  name: 'ghost',
  label: t('elements.simulator.decorator.ghostInput'),
  layout: 'horizontal',
  defaultValue: false,
  size: KitSize.S,
})

const showPlaceholderField = genField({
  as: FieldSwitcher,
  name: 'placeholder',
  label: t('elements.simulator.decorator.showPlaceholder'),
  layout: 'horizontal',
  defaultValue: true,
  size: KitSize.S,
})

interface IDecoratorSettingsProps {
  field: SimulatorField
  onUpdateField: (values: Partial<SimulatorField>) => void
}

const DecoratorSettings = ({ field, onUpdateField }: IDecoratorSettingsProps) => {
  const [placeholderValue, setPlaceholderValue] = useState(field.placeholderValue ?? '')

  useEffect(() => {
    setPlaceholderValue(field.placeholderValue ?? '')
  }, [field.placeholderValue])

  const onFinish = (values: DecoratorValues) => {
    onUpdateField(values)
  }

  const handlePlaceholderValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlaceholderValue(e.target.value)
    onUpdateField({ placeholderValue: e.target.value })
  }

  const initialValues: DecoratorValues = {
    ghost: field.ghost ?? false,
    placeholder: field.placeholder ?? true,
  }

  return (
    <div className={s.root}>
      <Form
        initialValues={initialValues}
        layout='vertical'
        name='decoratorSettings'
        onChangeValues={onFinish}
      >
        <Padding bottom gap sides top>
          <FormField config={ghostInputField} />
          <FormField config={showPlaceholderField} />
        </Padding>
      </Form>
      {field.placeholder !== false && (
        <Padding bottom gap sides>
          <div className={s.placeholderInput}>
            <input
              className={s.placeholderField}
              onChange={handlePlaceholderValueChange}
              placeholder={t('elements.simulator.decorator.placeholderValue')}
              type='text'
              value={placeholderValue}
            />
          </div>
        </Padding>
      )}
    </div>
  )
}

export default DecoratorSettings
